import { Component, OnInit, Inject, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogConfig, MatAutocomplete, MatDialogRef, MatDialog, MatAutocompleteTrigger } from '@angular/material';
import { DatePipe } from '@angular/common';
import { FormGroup, FormControl, FormBuilder } from '@angular/forms';
import { Observable, empty } from 'rxjs';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard/dashboard.service';
import { AuthService } from 'src/app/modules/login/services/auth/auth.service';
import { PatientsDirectoryService } from 'src/app/modules/patients/services/patients-directory/patients-directory.service';
import { startWith, map } from 'rxjs/operators';
import { keypressValidateNumber } from 'src/app/common/helper';
import { AlertMessageComponent } from 'src/app/modules/shared/components/alert-message/alert-message.component';
import { ConfirmDeletionComponent } from 'src/app/modules/shared/components/confirm-deletion/confirm-deletion.component';
import { DIR_DOCUMENT } from '@angular/cdk/bidi';

export interface Dentist {
  id: number;
  firstName: string;
  lastName: string;
}

export interface Procedure {
  id: number;
  name: string;
}

@Component({
  selector: 'ark-edit-clinical-note',
  templateUrl: './edit-clinical-note.component.html',
  styleUrls: ['./edit-clinical-note.component.scss']
})
export class EditClinicalNoteComponent implements OnInit {

  header = 'Patient Form';
  isEditable : boolean = false;
  hasError : boolean = false;
  datePipe = new DatePipe('en-US');
  errorMessage = null;
  clinic = '';
  dentistId = '';
  status : boolean = false;
  selectedProcedure = null;
  unlistedProcedure = null;
  customCollapsedHeight: string = '20px';
  customExpandedHeight: string = '20px';
  dialogConfig = new MatDialogConfig();
  formGroup: FormGroup;
  dentistControl = new FormControl();
  procedureControl = new FormControl();
  dentists: Dentist[] = [];
  procedures: Procedure[] = [];
  filteredDentists: Observable<Dentist[]>;
  filteredProcedures: Observable<Procedure[]>;
  @ViewChild('dentist') matAutocompleteDentist: MatAutocomplete;
  @ViewChild('dentist') dentist: MatAutocompleteTrigger;
  @ViewChild('procedure') procedure: MatAutocompleteTrigger;
  today = new Date();
  inputtedString = '';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<EditClinicalNoteComponent>,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private dashboard: DashboardService,
    private auth: AuthService,
    private clinicalNote: PatientsDirectoryService
  ) { 
    this.clinic = this.auth.getAuthUser().user_clinic.id;
    this.dashboard.getDentistList(this.clinic).subscribe(res => {
      res.forEach(element => {
        let obj: Dentist = {
          id: element.id, 
          firstName: element.first_name,
          lastName: element.last_name
        };
        this.dentists.push(obj);
      });
    }); 
    this.dashboard.getProcedures(this.clinic).subscribe(res => {
      res.forEach(element => {
        let obj: Procedure = {
          id: element.id, 
          name: element.title
        };
        this.procedures.push(obj);
      });
    });

    this.filteredProcedures = this.procedureControl.valueChanges
    .pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value.name),
      map(name => name ? this.filterProcedure(name) : this.procedures.slice())
    );
    this.filteredDentists = this.dentistControl.valueChanges
      .pipe(
        startWith(''),
        map(value => typeof value === 'string' ? value : value.name),
        map(name => name ? this.filterDentist(name) : this.dentists.slice())
      );
   }

  ngOnInit() {
    this.formGroup = this.formBuilder.group({
      date: [''],
      procedure: [''],
      dentist_id: [''],
      clinical_note: [''],
      amount_paid: [''],
    });
    this.status = true;
    this.isEditable = true;
    this.unlistedProcedure = this.data.data.procedure;
    this.dentistId = this.data.data.dentist ? this.data.data.dentist.id : null;
    this.formGroup.get('date').setValue(this.data.data.date);
    this.formGroup.get('procedure').setValue(this.data.data.procedure);
    this.formGroup.get('amount_paid').setValue(this.data.data.amount_paid);
    this.formGroup.get('clinical_note').setValue(this.data.data.clinical_note);
    if(this.data.data.dentist_id !== null){
      this.dentistControl.setValue(
        {
          id: this.data.data.dentist.id, 
          firstName: this.data.data.dentist.first_name, 
          lastName: this.data.data.dentist.last_name
        }
      );
    }
    if(this.data.data.procedure !== null){
      this.procedureControl.setValue(this.data.data.procedure);
    }
    this.dialogConfig.disableClose = true;
    this.dialogConfig.backdropClass = 'mat-dialog-backdrop';
  }

  filterDentist(name: string): Dentist[] {
    const filterValue = name.toLowerCase();
    return this.dentists.filter(option => option.lastName.toLowerCase().indexOf(filterValue) > -1 || option.firstName.toLowerCase().indexOf(filterValue) > -1);
  }

  filterProcedure(name: string): Procedure[] {
    const filterValue = name.toLowerCase();
    return this.procedures.filter(option => option.name.toLowerCase().indexOf(filterValue) > -1);
  }

  displayDentist(dentist?: any) : string | undefined {
    return dentist ? 'Dr. ' + dentist.firstName + ' ' + dentist.lastName : undefined;
  }

  displayProcedure(procedure?: any) : string | undefined {
    return procedure ? procedure : undefined;
  }

  numberTypeValidation(e) {
    return keypressValidateNumber(e);
  }

  close() {
    this.dialogRef.close();
  }

  getDentist(event) {
    this.dentistId = event.option.value.id;
    this.inputtedString = '';
  }

  updateClinicalNote() {
    this.status = !this.status;  
    if(this.isEditable) {
      this.disableInput();
      this.isEditable = false;
      this.header = 'Patient Form';
    } else {
      this.enableInput();
      this.isEditable = true;
      this.header = 'Update Patient Form';
    }
  }

  triggerErrors(err){
    Object.keys(this.formGroup.controls).forEach(key => {
      if (err[key]) {
        this.formGroup.controls[key].setErrors({'invalid': true});
        this.formGroup.controls[key].markAsTouched();
        this.formGroup.controls[key].markAsDirty();
      }
    });
  }

  disableInput() {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].disable();
    });
  }

  enableInput() {
    Object.keys(this.formGroup.controls).forEach(key => {
      this.formGroup.controls[key].enable();
    });
  }

  update() {
    let date = this.datePipe.transform(this.formGroup.value.date, 'yyyy-MM-dd');
    let data = {
      user_clinic_id: this.clinic,
      date: date,
      patient_id: this.data.data.patient_id,
      procedure: this.selectedProcedure ? this.selectedProcedure : this.unlistedProcedure,
      clinical_note: this.formGroup.value.clinical_note,
      amount_paid: this.formGroup.value.amount_paid,
      dentist_id: this.dentistId
      
    }
    if(this.inputtedString != '') {
      this.dialogConfig.data = {
        title: 'Oops!',
        message: 'The attending dentist field is invalid.',
        button: 'Okay',
        event: this.close
      };
      let dialogRef = this.dialog.open(AlertMessageComponent, this.dialogConfig);
    } else {
      this.clinicalNote.updatePatientClinicalNotes(this.data.data.id, data).subscribe(res => {
        if(res) {
          this.dialogRef.close(res);
          this.dialogConfig.data = {
            title: 'Success!',
            message: 'Patient form has been successfully updated.',
            button: 'Okay',
            event: this.close
          };
          let dialogRef = this.dialog.open(AlertMessageComponent, this.dialogConfig);
        }
      },(err => {
        this.dialogConfig.data = {
          title: 'Sorry!',
          message: err.error.message,
          button: 'Okay',
          event: this.close
        };
        
        if(this.formGroup.value.clinical_note == '' || this.formGroup.value.clinical_note == null) {
          this.hasError = true;
        } else {
          this.hasError = false;
        }

        let dialogRef = this.dialog.open(AlertMessageComponent, this.dialogConfig);
        this.errorMessage = err.error.errors;
        this.triggerErrors(err.error.errors);
      }));
    }
  }

  deleteClinicalNote() {
    const user = this.auth.getAuthUser();
    if(user.role_id === 'Owner/Dentist' || user.role_id === 'Dentist') {
      this.dialogConfig.data = {
        subject: 'delete this patient form'
      };
      this.dialogConfig.disableClose = true;
      this.dialogConfig.backdropClass = 'mat-dialog-backdrop';
      let dialogRef = this.dialog.open(ConfirmDeletionComponent, this.dialogConfig).afterClosed().subscribe(trigger => {
        if(trigger) {
          this.clinicalNote.deleteClinicalNote(this.data.data.id, this.clinic, this.data.data.patient_id).subscribe(res => {
            if(res) {
              this.dialogRef.close(res);
              this.dialogConfig.data = {
                title: 'Success!',
                message: 'Patient form has been successfully deleted.',
                button: 'Okay',
                event: this.close
              };
              let dialogRef = this.dialog.open(AlertMessageComponent, this.dialogConfig);
            }
          });
        }
      });
    } else {
      this.dialogConfig.data = {
        title: 'Sorry!',
        message: 'Deleting this patient form is restricted.',
        button: 'Okay',
        event: this.close
      };
      let dialogRef = this.dialog.open(AlertMessageComponent, this.dialogConfig);
    }
  }
  onFocusProcedure() {
    this.procedure._onChange(""); 
    this.procedure.openPanel();
  }

  getProcedure(event) {
    this.selectedProcedure = event.option.value;
  }
  getNewProcedure(e) {
    this.unlistedProcedure = e;
  }

  onKeydown(event) {
    if(event.keyCode == 8 || event.keyCode == 46) {
      this.inputtedString = '';
      this.dentistId = null;
    } else {
      this.inputtedString = null;
    }
  }

  onFocusDentist() {
    this.dentist._onChange(""); 
    this.dentist.openPanel();
  }
}
